/**
 * Command Bar Component
 * Natural language command interface for dashboard control
 */

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Dialog, DialogContent } from '../ui/dialog'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import { ScrollArea } from '../ui/scroll-area'
import { cn } from '../../lib/utils'
import { 
  Command, Search, ArrowRight, CheckCircle, Clock,
  FileText, Users, Calendar, MessageSquare, Settings,
  Plus, Filter, Target, Zap, Hash, AtSign,
  ChevronRight, Sparkles, Loader2
} from 'lucide-react'

interface CommandBarProps {
  onClose: () => void
  onExecute: (command: string) => Promise<void>
  recentCommands?: string[]
}

interface CommandSuggestion {
  id: string
  type: 'action' | 'search' | 'create' | 'navigate'
  title: string
  description?: string
  icon: any
  command: string
  category: string
}

const COMMAND_SUGGESTIONS: CommandSuggestion[] = [
  // Actions
  {
    id: 'create-task',
    type: 'create',
    title: 'Create new task',
    description: 'Add a new task to your workspace',
    icon: Plus,
    command: 'create task',
    category: 'Create'
  },
  {
    id: 'focus-mode',
    type: 'action',
    title: 'Enter focus mode',
    description: 'Minimize distractions and focus on your priority',
    icon: Target,
    command: 'focus mode',
    category: 'Productivity'
  },
  {
    id: 'show-urgent',
    type: 'search',
    title: 'Show urgent tasks',
    description: 'Filter tasks by urgency',
    icon: Zap,
    command: 'show urgent tasks',
    category: 'Filter'
  },
  {
    id: 'my-tasks',
    type: 'search',
    title: 'My tasks',
    description: 'Show all tasks assigned to you',
    icon: FileText,
    command: 'my tasks',
    category: 'Filter'
  },
  {
    id: 'team-status',
    type: 'navigate',
    title: 'Team status',
    description: 'View team member availability',
    icon: Users,
    command: 'team status',
    category: 'Navigate'
  },
  {
    id: 'today-calendar',
    type: 'navigate',
    title: "Today's calendar",
    description: 'View your schedule for today',
    icon: Calendar,
    command: 'calendar today',
    category: 'Navigate'
  },
  {
    id: 'chat',
    type: 'navigate',
    title: 'Open chat',
    description: 'Start or continue a conversation',
    icon: MessageSquare,
    command: 'chat',
    category: 'Navigate'
  },
  {
    id: 'settings',
    type: 'navigate',
    title: 'Settings',
    description: 'Configure your preferences',
    icon: Settings,
    command: 'settings',
    category: 'Navigate'
  }
]

const NATURAL_LANGUAGE_EXAMPLES = [
  "Show me what's due today",
  "Create a high priority task for API documentation",
  "Find all blocked tasks",
  "What should I work on next?",
  "Show team workload",
  "Tasks completed this week",
  "My overdue items",
  "Schedule meeting with team",
  "Add note about project status",
  "Find tasks with no assignee"
]

export default function CommandBar({
  onClose,
  onExecute,
  recentCommands = []
}: CommandBarProps) {
  const [query, setQuery] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showExamples, setShowExamples] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter suggestions based on query
  const filteredSuggestions = useMemo(() => {
    if (!query) return COMMAND_SUGGESTIONS
    
    const lowerQuery = query.toLowerCase()
    return COMMAND_SUGGESTIONS.filter(
      suggestion =>
        suggestion.title.toLowerCase().includes(lowerQuery) ||
        suggestion.command.toLowerCase().includes(lowerQuery) ||
        suggestion.description?.toLowerCase().includes(lowerQuery) ||
        suggestion.category.toLowerCase().includes(lowerQuery)
    )
  }, [query])

  // Group suggestions by category
  const groupedSuggestions = useMemo(() => {
    const groups: Record<string, CommandSuggestion[]> = {}
    filteredSuggestions.forEach(suggestion => {
      if (!groups[suggestion.category]) {
        groups[suggestion.category] = []
      }
      groups[suggestion.category].push(suggestion)
    })
    return groups
  }, [filteredSuggestions])

  // Handle command execution
  const handleExecute = async (command: string) => {
    if (!command.trim()) return
    
    setIsProcessing(true)
    try {
      await onExecute(command)
      onClose()
    } catch (error) {
      console.error('Command execution failed:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, filteredSuggestions.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredSuggestions[selectedIndex]) {
          handleExecute(filteredSuggestions[selectedIndex].command)
        } else {
          handleExecute(query)
        }
      } else if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedIndex, filteredSuggestions, query, onClose])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="p-0 max-w-2xl overflow-hidden">
        <div className="flex flex-col h-[600px]">
          {/* Header */}
          <div className="px-4 py-3 border-b bg-gradient-to-r from-krushr-primary/5 to-krushr-secondary/5">
            <div className="flex items-center gap-3">
              <Command className="w-5 h-5 text-krushr-primary" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a command or describe what you want to do..."
                className="flex-1 border-0 bg-transparent focus:ring-0 text-base placeholder:text-krushr-gray-400"
                disabled={isProcessing}
              />
              {isProcessing && (
                <Loader2 className="w-4 h-4 animate-spin text-krushr-primary" />
              )}
            </div>
            
            {/* Quick filters */}
            <div className="flex items-center gap-2 mt-2">
              <Badge 
                variant="outline" 
                className="text-xs cursor-pointer hover:bg-krushr-primary/10"
                onClick={() => setQuery('@me ')}
              >
                <AtSign className="w-3 h-3 mr-1" />
                My Items
              </Badge>
              <Badge 
                variant="outline" 
                className="text-xs cursor-pointer hover:bg-krushr-primary/10"
                onClick={() => setQuery('#urgent ')}
              >
                <Hash className="w-3 h-3 mr-1" />
                Urgent
              </Badge>
              <Badge 
                variant="outline" 
                className="text-xs cursor-pointer hover:bg-krushr-primary/10"
                onClick={() => setQuery('create ')}
              >
                <Plus className="w-3 h-3 mr-1" />
                Create
              </Badge>
              <Badge 
                variant="outline" 
                className="text-xs cursor-pointer hover:bg-krushr-primary/10"
                onClick={() => setShowExamples(!showExamples)}
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Examples
              </Badge>
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-4">
              {/* Natural language examples */}
              {showExamples && (
                <div className="mb-4">
                  <p className="text-xs text-krushr-gray-500 mb-2">Try these examples:</p>
                  <div className="space-y-1">
                    {NATURAL_LANGUAGE_EXAMPLES.map((example, idx) => (
                      <div
                        key={idx}
                        className="text-sm text-krushr-gray-600 hover:text-krushr-primary cursor-pointer py-1"
                        onClick={() => setQuery(example)}
                      >
                        "{example}"
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent commands */}
              {!query && recentCommands.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-krushr-gray-500 mb-2">Recent:</p>
                  <div className="space-y-1">
                    {recentCommands.slice(0, 5).map((cmd, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors",
                          "hover:bg-krushr-gray-50"
                        )}
                        onClick={() => handleExecute(cmd)}
                      >
                        <Clock className="w-3 h-3 text-krushr-gray-400" />
                        <span className="text-sm text-krushr-gray-700">{cmd}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Command suggestions */}
              {Object.entries(groupedSuggestions).map(([category, suggestions]) => (
                <div key={category} className="mb-4">
                  <p className="text-xs text-krushr-gray-500 mb-2">{category}</p>
                  <div className="space-y-1">
                    {suggestions.map((suggestion, idx) => {
                      const globalIndex = filteredSuggestions.indexOf(suggestion)
                      const isSelected = globalIndex === selectedIndex
                      const Icon = suggestion.icon

                      return (
                        <div
                          key={suggestion.id}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                            isSelected ? "bg-krushr-primary/10 border border-krushr-primary/30" : "hover:bg-krushr-gray-50"
                          )}
                          onClick={() => handleExecute(suggestion.command)}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                        >
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center",
                            isSelected ? "bg-krushr-primary text-white" : "bg-krushr-gray-100"
                          )}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-krushr-gray-900">
                              {suggestion.title}
                            </p>
                            {suggestion.description && (
                              <p className="text-xs text-krushr-gray-500">
                                {suggestion.description}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <ChevronRight className="w-4 h-4 text-krushr-gray-400" />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}

              {/* No results */}
              {query && filteredSuggestions.length === 0 && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">
                    No commands found for "{query}"
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try natural language or press Enter to execute
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 py-3 border-t bg-krushr-gray-50">
            <div className="flex items-center justify-between text-xs text-krushr-gray-500">
              <div className="flex items-center gap-4">
                <span><kbd className="px-1.5 py-0.5 bg-white rounded border">↑↓</kbd> Navigate</span>
                <span><kbd className="px-1.5 py-0.5 bg-white rounded border">↵</kbd> Execute</span>
                <span><kbd className="px-1.5 py-0.5 bg-white rounded border">Esc</kbd> Close</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-3 h-3" />
                <span>AI-powered natural language</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}